/**
 * Every table's column layout, persisted.
 *
 * **This is the third module allowed to touch `localStorage`, and the split is
 * deliberate.** `src/auth/session-store.ts` holds the session,
 * `src/lib/theme-store.ts` holds one theme name, and this holds column layouts
 * and can never hold anything else.
 * `docs/decisions/0003-ui-internal-persists-one-id-token.md` exists to keep
 * *credentials* out of Web Storage — the rule the lint message states is
 * "tokens stay in memory" — and a column order is not a credential. Widening
 * the exemption to a named third file, rather than relaxing the rule, is what
 * keeps the list of things this origin persists short and readable. See
 * `docs/decisions/0007-a-table-layout-persists-per-browser.md`.
 *
 * `.oxlintrc.json` exempts this file for `localStorage` and nothing else, the
 * same narrowing the other two get: `sessionStorage`, `indexedDB`, `caches`,
 * `document.cookie`, every `window.`-prefixed spelling and the computed form
 * all still fail the gate here.
 *
 * The preset began with core-service's shape. super-ui
 * saves a `{ order, hidden }` preset per table under `table_columns` in
 * a per-user bucket that endpoint owns. This app has no such endpoint, so the
 * bucket lives in one key of this browser instead — which is a real
 * difference, and the reason the ADR above exists: a layout set here follows
 * neither the reader to another machine nor their other console. This app
 * also stores `frozen` and `widths`; a future endpoint must accept or strip them.
 *
 * Nothing here trusts what it reads. The key is editable by hand and outlives
 * the screens that wrote it, so every value is checked on the way out and
 * anything unreadable reads as "no layout" — a table with a stale order must
 * cost the reader their order, not the screen.
 *
 * Neither entry point throws. A browser with storage disabled or a full quota
 * must leave the console working: a read answers "no layout" and a write
 * answers false, so the caller can say the layout is not held rather than
 * losing the screen to an exception.
 */

import {
  MAX_COLUMN_KEYS,
  clampColumnWidth,
  MAX_FROZEN_COLUMNS,
  type TableColumnPreset
} from './table-columns.ts'

/**
 * The only key this module writes.
 *
 * One key for every table rather than one per table, because a reader who
 * clears it clears their layouts, and a per-table key leaves an origin full of
 * names nothing reads.
 */
export const TABLE_COLUMNS_KEY = 'ui-internal.table-columns'

/** One table's name in the store. Lowercase snake_case, as core-service validates. */
export type TableKey = string

/** Every stored layout, by table. */
export type TableColumnStore = Readonly<Record<TableKey, TableColumnPreset>>

/**
 * Whether a value is a plain object, which is what the store and every preset is.
 *
 * Exported so the settings answer from the backend is checked by the same rule.
 *
 * @param value - A value read out of storage, or out of a response.
 * @returns True for a non-array, non-null object.
 */
export function isPlainObject(
  value: unknown
): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * One stored list as the column keys it actually holds.
 *
 * A list that is not a list, or one holding a number, reaches the layout
 * functions otherwise and renders a header for a column that does not exist.
 *
 * @param value - A stored `order` or `hidden`.
 * @returns The string entries, or null when there is no usable list.
 */
function readKeyList(value: unknown): string[] | null {
  if (!Array.isArray(value)) {
    return null
  }

  return value
    .filter((entry): entry is string => typeof entry === 'string')
    .slice(0, MAX_COLUMN_KEYS)
}

/**
 * One stored layout, checked.
 *
 * @param stored - A value read out of the store.
 * @returns The preset, carrying only the lists that were usable.
 */
function readPreset(stored: unknown): TableColumnPreset {
  if (!isPlainObject(stored)) {
    return {}
  }

  const order = readKeyList(stored.order)
  const hidden = readKeyList(stored.hidden)
  const frozen = readKeyList(stored.frozen)?.slice(0, MAX_FROZEN_COLUMNS)
  const widths = isPlainObject(stored.widths)
    ? Object.fromEntries(
        Object.entries(stored.widths)
          .filter(
            (entry): entry is [string, number] =>
              typeof entry[1] === 'number' && Number.isFinite(entry[1])
          )
          .slice(0, MAX_COLUMN_KEYS)
          .map(([key, width]) => [key, clampColumnWidth(width)])
      )
    : null

  return {
    ...(order ? { order } : {}),
    ...(hidden ? { hidden } : {}),
    ...(frozen ? { frozen } : {}),
    ...(widths ? { widths } : {})
  }
}

/**
 * A whole store, checked, from a value of unknown shape.
 *
 * Shared by this browser's key and by the settings document the backend
 * returns, because both hold the same bag of layouts and neither is trusted.
 *
 * @param parsed - A parsed JSON value.
 * @returns The store, or null when the value is not a store at all.
 */
export function parseColumnStore(parsed: unknown): TableColumnStore | null {
  if (!isPlainObject(parsed)) {
    return null
  }

  const store: Record<TableKey, TableColumnPreset> = {}
  for (const [key, value] of Object.entries(parsed)) {
    store[key] = readPreset(value)
  }

  return store
}

/**
 * Every layout this browser holds, or null when it holds no key at all.
 *
 * Null and an empty store differ: the settings sync uploads a browser's
 * layouts only when there are some, and never invents any.
 *
 * @returns The store, or null when the key is absent or unreadable.
 */
export function readColumnStore(): TableColumnStore | null {
  let raw: string | null = null
  try {
    raw = localStorage.getItem(TABLE_COLUMNS_KEY)
  } catch {
    // Storage disabled. Every table renders its declared layout.
    return null
  }
  if (raw === null) {
    return null
  }

  try {
    return parseColumnStore(JSON.parse(raw))
  } catch {
    // Hand-edited, or half-written by a tab that died mid-save.
    return null
  }
}

/**
 * Replace every layout this browser holds.
 *
 * The settings store calls this on every change and when the server answers,
 * because the server's copy is the record and this key is only the cache the
 * next load paints from.
 *
 * @param store - Every layout to hold.
 * @returns True when the store was held, false when storage refused it.
 */
export function writeColumnStore(store: TableColumnStore): boolean {
  try {
    // An empty store removes the key: a reader with no layout leaves nothing
    // behind.
    if (Object.keys(store).length === 0) {
      localStorage.removeItem(TABLE_COLUMNS_KEY)

      return true
    }
    localStorage.setItem(TABLE_COLUMNS_KEY, JSON.stringify(store))

    return true
  } catch {
    // Quota, or storage disabled. The layout applies to this tab and simply
    // does not survive the next reload. Reported rather than swallowed, so the
    // caller decides whether a reader is told.
    return false
  }
}
